import * as React from 'react';
import { Text, View } from 'react-native';

import type * as api from '@gitbook/api-client';
import * as hiveCore from '@gitbook/hive-core';
import {
    BaseCard,
    flex,
    Interactive,
    OneLineText,
    Responsive,
    spacing,
    themedFonts,
    useCurrentTheme,
} from '@gitbook/react-uikit';
import { LoadingIndicator, renderIcon } from '@gitbook/spine-react';

import { LayoutSidebarContext } from '../../../AppLayout';
import {
    PageIcon,
    spaceLayout,
    type UIResolvedContentReference,
    useContentReference,
    useContentRefNavigationProps,
} from '../../../Components';
import { useNavigation } from '../../../navigation';

const ResponsiveCard = Responsive.createResponsiveComponent(BaseCard);
const InteractiveOneLineText = Interactive.createInteractiveComponent(OneLineText);

/**
 * A Grid of links to sub-pages when a Page has no content
 */
export function ReadOnlyPageGroupSummary(props: {
    subPages: hiveCore.RevisionPage[];
    revision: hiveCore.Revision;
}) {
    const { subPages, revision } = props;
    const { desktopSidebarWidth } = React.useContext(LayoutSidebarContext);
    const [, font] = useCurrentTheme(themedFonts);

    if (subPages.length === 0) {
        return null;
    }

    return (
        <View>
            <Text style={[font.Body, font.text]}>Here are the articles in this section:</Text>
            <Responsive.View
                style={[spacing.Padding.top600, flex.wrap]}
                desktopStyle={flex.row}
                customBreakpointsStyles={{
                    [spaceLayout.pageSwitchThreadhold(desktopSidebarWidth)]: flex.column,
                }}
            >
                {subPages.map((page, i) => (
                    <React.Suspense
                        fallback={
                            <PageCardImplementation
                                page={page}
                                revision={revision}
                                index={i}
                                totalPageNumber={subPages.length}
                            />
                        }
                        key={page.id}
                    >
                        <PageCardWithSuspense
                            page={page}
                            revision={revision}
                            index={i}
                            totalPageNumber={subPages.length}
                        />
                    </React.Suspense>
                ))}
            </Responsive.View>
        </View>
    );
}

function PageCardWithSuspense(props: {
    page: hiveCore.RevisionPage;
    revision: hiveCore.Revision;
    index: number;
    totalPageNumber: number;
}) {
    const { page } = props;

    const contentRef = useContentReference(
        page.type === hiveCore.RevisionPageType.Link ? (page.target as api.ContentRef) : undefined
    );

    return <PageCardImplementation {...props} contentRef={contentRef} />;
}

function PageCardImplementation(props: {
    page: hiveCore.RevisionPage;
    revision: hiveCore.Revision;
    index: number;
    totalPageNumber: number;
    contentRef?: UIResolvedContentReference;
}) {
    const { page, revision, index, totalPageNumber, contentRef } = props;
    const { desktopSidebarWidth } = React.useContext(LayoutSidebarContext);
    const { getNavigationLinkProps } = useNavigation();
    const [, font] = useCurrentTheme(themedFonts);

    const endWith2Cards = totalPageNumber % 2 === 0;
    const contentRefNavProps = useContentRefNavigationProps(
        page.type === hiveCore.RevisionPageType.Link ? (contentRef ?? null) : null
    );

    return (
        <ResponsiveCard
            {...(page.type === hiveCore.RevisionPageType.Link
                ? contentRefNavProps
                : getNavigationLinkProps({
                      screen: {
                          page: hiveCore.getAbsolutePagePathInRevision(revision, page.id),
                      },
                  }))}
            style={[
                flex.alignItemsCenter,
                spacing.Padding.all400,
                flex.flex1,
                { minWidth: `calc(50% - ${spacing.small}px)` },
            ]}
            desktopStyle={[
                index % 2 === 0
                    ? endWith2Cards
                        ? spacing.Margin.right200
                        : null
                    : spacing.Margin.left200,
                index < totalPageNumber - (endWith2Cards ? 2 : 1) ? spacing.Margin.bottom200 : null,
            ]}
            customBreakpointsStyles={{
                [spaceLayout.pageSwitchThreadhold(desktopSidebarWidth)]: [
                    spacing.Margin.horizontal0,
                    index < totalPageNumber - 1 ? spacing.Margin.bottom200 : null,
                ],
            }}
        >
            <PageIcon page={page} size={24} className="m-nudge-icon mr-4" />
            <InteractiveOneLineText.Child
                style={[font.Body, font.Medium, font.text]}
                activeStyle={font.primary}
            >
                {page.linkTitle || page.title}
            </InteractiveOneLineText.Child>
            {page.type === hiveCore.RevisionPageType.Link ? (
                <div className="ml-4 flex">
                    {contentRef ? (
                        contentRef.secondaryIcon ? (
                            renderIcon(contentRef.secondaryIcon, { size: 18 })
                        ) : null
                    ) : (
                        <LoadingIndicator size={18} />
                    )}
                </div>
            ) : null}
        </ResponsiveCard>
    );
}
